import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { getGroupIncomingRequests, updateRequestStatus } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Inbox, Package, MapPin, Clock, CheckCircle, XCircle,
  ArrowRight, AlertTriangle, Building2, Filter
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  awaiting_logistics_confirmation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
};

const STATUS_ICONS = {
  pending: Clock,
  approved: CheckCircle,
  awaiting_logistics_confirmation: Clock,
  rejected: XCircle,
  completed: CheckCircle
};

const STATUS_LABELS = {
  awaiting_logistics_confirmation: 'Logistik-Bestätigung'
};

export const GroupRequestsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });

  useEffect(() => {
    if (user) loadRequests();
  }, [user]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getGroupIncomingRequests({});
      setRequests(res.data);
    } catch (error) {
      console.error('Failed to load group requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, status, extra = {}) => {
    try {
      await updateRequestStatus(requestId, { status, ...extra });
      toast.success(t(`requests.action.${status}Success`));
      loadRequests();
      setActionDialog({ open: false, action: null });
      setSelectedRequest(null);
    } catch (error) {
      console.error('Failed to update request:', error);
      toast.error(error.response?.data?.detail || t('requests.action.failed'));
    }
  };

  const filtered = statusFilter === 'all'
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Building2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t('requests.loginRequired')}</h1>
          <p className="text-muted-foreground mb-6">{t('requests.loginHint')}</p>
          <Button asChild className="bg-orange-500 hover:bg-orange-600">
            <Link to="/login">{t('nav.login')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="group-requests-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-manrope text-3xl font-bold">{t('groupRequests.title')}</h1>
            <p className="text-muted-foreground mt-1">{t('groupRequests.subtitle')}</p>
          </div>

          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <Badge className="bg-orange-500 text-white px-3 py-1" data-testid="pending-count-badge">
                {pendingCount} {t('groupRequests.pending')}
              </Badge>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('groupRequests.filterAll')}</SelectItem>
                <SelectItem value="pending">{t('status.pending')}</SelectItem>
                <SelectItem value="approved">{t('status.approved')}</SelectItem>
                <SelectItem value="awaiting_logistics_confirmation">Logistik-Bestätigung</SelectItem>
                <SelectItem value="rejected">{t('status.rejected')}</SelectItem>
                <SelectItem value="completed">{t('status.completed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="group-requests-empty">
            <Inbox className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('groupRequests.empty')}</h2>
            <p className="text-muted-foreground">{t('groupRequests.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="group-requests-list">
            {filtered.map((request) => {
              const StatusIcon = STATUS_ICONS[request.status] || Clock;
              const statusLabel = STATUS_LABELS[request.status] || t(`status.${request.status}`, { defaultValue: request.status });
              return (
                <Card key={request.request_id} className="overflow-hidden" data-testid={`group-request-card-${request.request_id}`}>
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Link
                            to={`/material/${request.material?.material_id}`}
                            className="font-semibold hover:text-orange-500 transition-colors"
                            data-testid="group-request-material-link"
                          >
                            {request.material?.common_title}
                          </Link>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            <Badge className={cn(STATUS_COLORS[request.status])} data-testid="group-request-status-badge">
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusLabel}
                            </Badge>
                            {request.is_escalated && (
                              <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" data-testid="group-request-escalated-badge">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {t('admin.escalatedRequests') || 'Eskaliert'}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Package className="h-4 w-4" />
                            {request.requested_qty} {request.sku?.uom}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {request.site?.site_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {new Date(request.requested_at).toLocaleDateString()}
                          </span>
                        </div>

                        {request.requester && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {t('requests.from')}: <span className="font-medium text-foreground">{request.requester.name}</span>
                          </p>
                        )}

                        {request.message && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 italic">
                            "{request.message}"
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 p-4 bg-slate-50 dark:bg-slate-800/50 md:flex-col md:justify-center">
                        <Button variant="outline" size="sm" onClick={() => setSelectedRequest(request)} data-testid="group-view-request-btn">
                          {t('requests.viewDetails')}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>

                        {request.status === 'pending' && (
                          <div className="flex gap-2 md:flex-col">
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => {
                                setSelectedRequest(request);
                                setActionDialog({ open: true, action: 'approve' });
                              }}
                              data-testid="group-approve-request-btn"
                            >
                              {t('requests.action.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedRequest(request);
                                setActionDialog({ open: true, action: 'reject' });
                              }}
                              data-testid="group-reject-request-btn"
                            >
                              {t('requests.action.reject')}
                            </Button>
                          </div>
                        )}

                        {request.status === 'approved' && (
                          <Button
                            size="sm"
                            className="bg-blue-500 hover:bg-blue-600"
                            onClick={() => {
                              setSelectedRequest(request);
                              setActionDialog({ open: true, action: 'complete' });
                            }}
                            data-testid="group-complete-request-btn"
                          >
                            {t('requests.action.complete')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={!!selectedRequest && !actionDialog.open} onOpenChange={(open) => !open && setSelectedRequest(null)}>
          <DialogContent className="max-w-lg" data-testid="group-request-detail-dialog">
            <DialogHeader>
              <DialogTitle>{t('requests.details')}</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <h4 className="font-semibold mb-2">{selectedRequest.material?.common_title}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('requests.detail.article')}: {selectedRequest.sku?.article_number}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('requests.detail.quantity')}</p>
                    <p className="font-semibold">{selectedRequest.requested_qty} {selectedRequest.sku?.uom}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('requests.detail.type')}</p>
                    <p className="font-semibold">
                      {t(`request.type.${selectedRequest.request_type === 'internal_transfer' ? 'internal' : 'external'}`)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('requests.detail.requestedBy')}</p>
                    <p className="font-semibold">{selectedRequest.requester?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('requests.detail.date')}</p>
                    <p className="font-semibold">{new Date(selectedRequest.requested_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('groupRequests.site')}</p>
                    <p className="font-semibold">{selectedRequest.site?.site_name}</p>
                  </div>
                </div>
                {selectedRequest.message && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('requests.detail.message')}</p>
                    <p className="text-sm p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">{selectedRequest.message}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Action Dialog */}
        <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: null })}>
          <DialogContent data-testid="group-request-action-dialog">
            <DialogHeader>
              <DialogTitle>
                {actionDialog.action === 'approve' && t('requests.action.approveTitle')}
                {actionDialog.action === 'reject' && t('requests.action.rejectTitle')}
                {actionDialog.action === 'complete' && t('requests.action.completeTitle')}
              </DialogTitle>
              <DialogDescription>
                {actionDialog.action === 'approve' && t('requests.action.approveDesc')}
                {actionDialog.action === 'reject' && t('requests.action.rejectDesc')}
                {actionDialog.action === 'complete' && t('requests.action.completeDesc')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialog({ open: false, action: null })} data-testid="group-action-cancel-btn">
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="group-action-confirm-btn"
                className={cn(
                  actionDialog.action === 'reject' && 'bg-red-500 hover:bg-red-600',
                  actionDialog.action === 'approve' && 'bg-blue-500 hover:bg-blue-600',
                  actionDialog.action === 'complete' && 'bg-green-500 hover:bg-green-600'
                )}
                onClick={() => {
                  const statusMap = { approve: 'approved', reject: 'rejected', complete: 'completed' };
                  handleStatusUpdate(selectedRequest.request_id, statusMap[actionDialog.action]);
                }}
              >
                {actionDialog.action === 'approve' && t('requests.action.approve')}
                {actionDialog.action === 'reject' && t('requests.action.reject')}
                {actionDialog.action === 'complete' && t('requests.action.complete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
